import { TargetType } from '@mulesoft/monitoring-center-components/lib/models/resource/hybrid'
import { Address } from '@mulesoft/monitoring-center-components/lib/models/resource/target'

export type User = {
  createdAt: string
  email: string
  enabled: boolean
  firstName: string
  id: string
  idprovider_id: string
  lastLogin: string
  lastName: string
  mfaVerificationExcluded: boolean
  mfaVerifiersConfigured: string
  organizationId: string
  type: string
  updatedAt: string
  username: string
}

export type ApplicationId = {
  id: string
}

export type Application = {
  id: string
  hybridId?: string
  name?: string
  status: string
  targetType: string
  targetSubtype?: string
  targetId?: number,
  serverName?: string,
  muleVersion?: string
}

export type SettingsSimpleApplication = {
  applicationId: string
  applicationName: string
}

export type SettingsDeployment = {
  isLoading: boolean
  isChanging: boolean
  deployment: {
    newAppsAgentEnabled?: boolean
  }
}

export type CloudHubSettings = {
  organizationId: string
  settings: SettingsDeployment
}

export type PutSettingsApplication = {
  applicationId: string
  applicationName: string
  properties?: { [key: string]: string }
}

export type ARMApplication = Application & {
  target: {
    type: string
    subtype?: string
    id?: number
    name?: string
  }
  artifact: {
    lastUpdateTime: string | number
    name: string
    fileName: string
  }
  lastReportedStatus: string
  uptime?: number
  details: ARMApplicationDetails
  gatewayVersion?: string
}

export interface ARMApplicationDetails {
  domain?: string
  serverArtifacts?: ARMServerArtifact[]
}

export interface ARMServerArtifact {
  target: ARMTarget
  lastReportedStatus: string
  message: string;
}

export interface ARMHybridServerArtifact {
  id: number
  serverId: number
  lastReportedStatus: string
  message: string;
}

export interface ARMTarget {
  id: number
  type: string
  name: string
  status: string
  gatewayVersion?: string
  muleVersion?: string
  servers?: ARMTarget[]
}

export type RTFApplication = {
  application: {
    configuration?: {
      'mule.agent.logging.service'?: {
        scopeLoggingConfigurations: ScopeLogging[]
      }
    },
    status: string,
    vCores?: number
  },
  id: string,
  name?: string,
  artifact?: {
    lastUpdateTime: number,
    createTime: number,
    name: string
  },
  lastModifiedDate: number,
  target: {
    type?: string,
    subtype?: string,
    provider: string,
    id?: string,
    targetId?: string,
    name?: string,
    status?: string,
    deploymentSettings: {
      http: {
        inbound: {
          publicUrl: string
        }
      },
      resources?: {
        cpu: {
          limit: string,
          reserved: string
        },
        memory: {
          limit: string,
          reserved: string
        }
      }
      runtimeVersion: string
    },
    replicas: number
  },
  replicas: any[],
  status: string,
}

// cloudhub
export type ARMApplicationDetail = {
  domain: string
  fullDomain: string
  fileName: string
  deploymentUpdateStatus: string
  status: string
  insightsReplayDataRegion: string
  lastUpdateTime: number
  muleVersion: {
    version: string
    updateId: string
  }
  workers: {
    amount: number
    type: {
      name: string
      weight: number
      cpu: string
      memory: string
    }
  }
  properties: { [key: string]: string }
  logLevels: ARMLogLevel[]
  versionId: string
  target?: {
    type: TargetType
  }
}

export type ARMLogLevel = {
  packageName: string
  level: string
}

export type PutARMApplicationDetail = {
  domain: string
  fullDomain?: string
  fileName?: string
  deploymentUpdateStatus?: string
  status?: string
  insightsReplayDataRegion?: string
  lastUpdateTime?: number
  muleVersion?: {
    version: string
    updateId: string
  }
  workers?: {
    amount: number
    type: {
      name: string
      weight: number
      cpu: string
      memory: string
    }
  }
  properties?: { [key: string]: string }
  logLevels?: ARMLogLevel[]
}

export type ARMHybridDetail = {
  id: number
  name: string
  lastReportedStatus: string
  serverArtifacts?: ARMHybridServerArtifact[];
  artifact: {
    id: number
    name: string
    timeUpdated?: number
  }
  target: ARMTarget
}

export type Deployment = {
  audit?: Audit
  id: number
  applicationId: string | null
  applicationName: string
  gatewayVersion: string
  environmentName: string
  environmentId: string
  targetId: string | null
  targetName: string | null
  deploymentId: string | null
  updatedDate: string | null
  type: string
  apiId: string | number
}

export type TargetDeployment = {
  id: number
  artifact: {
    fileChecksum: string
    fileName: string
    lastUpdateTime: number
    name: string
  }
  lastReportedStatus: string
}

export type ARMServer = {
  id: string
  name: string
  type: TargetType
  status: string
  deployments: TargetDeployment[]
  details: ARMDetail
}

export type ARMDetail = {
  servers?: ARMServer[]
  addresses?: Address[]
  agentVersion?: string,
  runtimeVersion?: string,
  type?: string
}

export type ARMComponent = {
  component: {
    id: number
    name: string
  }
  configuration: {
    properties: [{
      name: string
      value: string
    }]
  }
  enabled: true
}

export type ARMLoggingComponent = {
  component: {
    id: number
    name: string
  }
  enabled: boolean
  configuration: ARMLoggingConfiguration
}

export type ARMLoggingConfiguration = {
  applicationLogLevels: ApplicationLogLevel[]
}

export type ApplicationLogLevel = {
  artifactName: string
  scopeLoggingConfigurations: ScopeLogging[]
}

export type ScopeLogging = {
  logLevel: string
  scope: string
}

export type APIStatus = {
  fullDomain: string
  lastActiveDate: any
  status: string
}

// ------------------------------------------- API Manager models ------------------------------------------------------

export type Audit = {
  created: Created
  updated: Updated
}

export type Created = {
  date?: string
}

export type Updated = {
  date?: string
}

export type RuleOfApplication = {
  organizationId: string
  environmentId: string
  range?: {
    from?: string,
    to?: string
  }
}

export type DetailedPolicy = {
  id: number,
  groupId?: string,
  assetId?: string,
  assetVersion?: string,
  policyTemplateId?: string,
  pointcutData?: string | { methodRegex?: string, uriTemplateRegex?: string },
  ruleOfApplication?: RuleOfApplication,
  order?: number,
  type?: string,
  name?: string,
  category?: string
}

export type DetailedAPI = {
  apiId: number,
  assetId: string,
  assetVersion: string,
  environmentId: string,
  exchangeAssetName?: string,
  groupId: string,
  id: number,
  instanceLabel?: string,
  masterOrganizationId: string,
  muleVersion4OrAbove?: string,
  name: string,
  organizationId: string,
  providerId?: string,
  policies?: DetailedPolicy[],
  productVersion: string
}

export type API = {
  audit: Audit
  masterOrganizationId: string
  organizationId: string
  id: string | number
  instanceLabel?: any
  groupId: string
  assetId: string | number
  assetVersion: string
  productVersion: string
  description?: string | null
  tags?: any
  order: number
  deprecated: boolean
  lastActiveDate: string | null
  endpointUri?: any
  environmentId: string
  isPublic: boolean
  lastActiveDelta?: number
  pinned: boolean
  activeContractsCount: number
  autodiscoveryInstanceName: string
  exchangeAssetName?: string
  applicationName?: string
  technology?: string
  httpMetrics?: boolean
}

export type Asset = {
  audit: any
  masterOrganizationId: string
  organizationId: string
  id: string | number
  name: string
  exchangeAssetName: string
  groupId: string
  assetId: string
  apis: API[]
  totalApis?: number
  autodiscoveryApiName?: string
  targetName?: string
  targetType?: TargetType
}

export type AssetResponse = {
  assets: Asset[]
  total: number
}

export type TemplatePolicy = {
  allowMultiple?: boolean
  category: string
  configurationArrayName?: string
  description: string
  gatewayCompatibility: string
  id: string
  name: string
  requiresConnectivity: boolean
  resourceLevelSupported: boolean
  standalone: boolean
  supportedPoliciesVersions: string
  type: string
}

export type ExchangeTemplatePolicy = {
  id: string | number
  groupId: string
  assetId: string
  version: string
  name: string
  minMuleVersion?: string
}

export type MessageLoggingPolicyConfiguration = {
  itemData: {
    category?: string
    firstSection: boolean
    level: string
    message: string
    secondSection: boolean
  }
  itemName: string
}

export type SplitMessageLoggingPolicyConfiguration = {
  loggingConfiguration: MessageLoggingPolicyConfiguration[]
}

export type APIPolicyConfiguration = MessageLoggingPolicyConfiguration[] | SplitMessageLoggingPolicyConfiguration | any

export const isSplitMessageLoggingPolicyConfiguration =
    (policyConfiguration: APIPolicyConfiguration): policyConfiguration is SplitMessageLoggingPolicyConfiguration => {
  return !!(policyConfiguration as SplitMessageLoggingPolicyConfiguration).loggingConfiguration
}

export type APIPolicyImplementationAsset = {
  assetId: string
  assetVersion: string
  groupId: string
  minRuntimeVersion?: string
  version?: string
}

export type APIPolicy = {
  apiId: number
  assetId: string | null
  assetVersion: string | null
  audit: Audit
  configurationData: APIPolicyConfiguration
  disabled: boolean
  id: number
  groupId: string | null
  masterOrganizationId: string
  order?: number
  organizationId: string
  pointcutData: string | null
  policyTemplateId: string
  policyVersion?: string
  type: string
  implementationAsset?: APIPolicyImplementationAsset
}

export type Endpoint = {
  audit?: Audit
  id: string | number
  type: string
  uri: string
  proxyUri: string | null
  proxyRegistrationUri: string | null
  lastActiveDate: string | null
  isCloudHub: boolean | null
  deploymentType: string
  policiesVersion: string | null
  referencesUserDomain: string | boolean | null
  muleVersion4OrAbove: boolean | null
  responseTimeout: string | null
  wsdlConfig: string | null | { name: string, port: string, namespace: string }
}

export type Gateway = {
  policiesVersion: string
  supportsOrdering: boolean
}

export type APIDetail = {
  audit?: Audit
  masterOrganizationId: string
  organizationId: string
  id: number | string
  instanceLabel: string | null
  groupId: string
  assetId: string
  assetVersion: string
  productVersion: string
  deprecated: boolean
  environmentId: string
  endpoint: Endpoint
  deployment: Deployment | null
  gateway?: Gateway
  lastActiveDate: string | null
  lastActiveDelta?: number
  autodiscoveryInstanceName: string
  apiStatus?: APIStatus
  apiId?: string
  technology?: string
}

export type APIDetailWithStatus = APIDetail & {
  apiStatus: APIStatus
}

export type Environment = {
  id: string,
  name: string,
  organizationId: string,
  isProduction: boolean,
  type: string,
  clientId: string,
}

// ------------------------------------------- UI API Models ------------------------------------------------------

export type LogPointsConnectors = {
  mule3Icons: ConnectorIcon[]
  mule4Icons: ConnectorIcon[]
}

export type ConnectorIcon = {
  id: string // connector assetId from exchange
  name: string // connector name from exchange
  icon: string // connector externalUrl from exchange
  packages: string[] // connector logging packages
}

// ------------------------------------------- Alerts API Models ------------------------------------------------------

export type AlertsLimit = {
  max: number,
  exceeded: boolean
}

export type AlertsMeta = {
  enabled: boolean,
  limit: AlertsLimit
}

export type AlertsMetaResponse = {
  basic: AlertsMeta & {
    api: {
      enabled: boolean
      responseCodesLimit: number
    }
  },
  complex: AlertsMeta
}
